// Module frontend surface.
//
// The shell discovers this file automatically (modules/*/frontend/index.js) and
// merges what it exports. Adding this module needs no edit to the shell.
//
// The first route is `/assets`, and it opens on the TREE rather than on a list
// of everything: a plant is walked, not scrolled, and an alphabetical list of
// four thousand nodes answers a question nobody asked.

export const routes = [
  {
    path: '/assets',
    name: 'assets',
    component: () => import('./pages/AssetsPage.vue'),
    meta: { permission: 'assets:read', title: 'assets.title' },
  },
  {
    path: '/assets/plant',
    name: 'assets-plant',
    component: () => import('./pages/PlantPage.vue'),
    meta: { permission: 'assets:read', title: 'assets.plant.title' },
  },
  {
    path: '/assets/new',
    name: 'assets-new',
    component: () => import('./pages/AssetFormPage.vue'),
    meta: { permission: 'assets:write', title: 'assets.form.newTitle' },
  },
  {
    path: '/assets/:id',
    name: 'assets-asset',
    component: () => import('./pages/AssetPage.vue'),
    meta: { permission: 'assets:read', title: 'assets.title' },
  },
  {
    path: '/assets/:id/edit',
    name: 'assets-edit',
    component: () => import('./pages/AssetFormPage.vue'),
    meta: { permission: 'assets:write', title: 'assets.form.editTitle' },
  },
]

// Two entries, and the second one is where a manager lives: the census is
// walked when somebody is looking for a machine, and the plant screen is opened
// every morning to see what is down.
export const navigation = [
  {
    to: '/assets',
    labelKey: 'assets.title',
    permission: 'assets:read',
    icon: 'tree',
    group: 'business',
    children: [
      { to: '/assets', labelKey: 'assets.nav.census', permission: 'assets:read' },
      { to: '/assets/plant', labelKey: 'assets.plant.title', permission: 'assets:read' },
    ],
  },
]

// Strings this module contributes, merged per locale. Four languages from the
// first commit (section 16): a module that ships two is a module that cannot be
// sold to the third client.
const baseMessages = {
  es: {
    // Como se llama este modulo en las pantallas de la plataforma
    // (el selector de la demo, los roles, la auditoria). Sin esto
    // salen listando el slug en ingles.
    modules: { assets: 'Activos' },
    assets: {
      title: 'Activos',
      // What the assistant's card says (backend/assistant.js, log_meter_reading).
      assistant: {
        reading: {
          action: 'Apuntar una lectura de contador',
          summary: 'Lectura de {value} {unit} en {meter}',
          field: { meter: 'Contador', value: 'Marca', current: 'Ahora en', readAt: 'Leída el', note: 'Nota' },
          where: 'Máquina',
          notes: { noMeter: 'No se encuentra ese contador; míralo en la ficha de la máquina', below: 'Menor que el contador: la lectura se guarda y el contador no se mueve' },
          outcome: 'Lectura de {value} apuntada',
          outcomeKept: 'Lectura de {value} guardada; el contador no se ha movido',
        },
      },
      dashboard: {
        stopped: 'Máquinas paradas',
        stoppedHelp: 'Cuántos equipos están parados en este momento, contando averías y paros programados.',
        expiring: 'Papeles que caducan',
        expiringHelp: 'Certificados, garantías e inspecciones que vencen dentro del plazo de aviso configurado.',
        census: 'Equipos en el censo',
        censusHelp: 'Cuántos activos hay dados de alta, sin contar los que están de baja.',
        stoppedNow: 'Parado ahora mismo',
        stoppedNowHelp: 'Los equipos parados en este momento, con las horas que llevan así. Estira la tarjeta y caben más.',
        worst: 'Las que más cuestan',
        worstHelp: 'Los equipos con más horas de parada por avería en el último año, empezando por el peor.',
      },
      subtitle: 'Qué máquinas hay, dónde están y cómo se portan',
      nav: { census: 'Equipos e instalaciones' },
      suggestions: {
        whatIsDown: '¿Qué está parado ahora mismo?',
        worstMachine: '¿Qué máquina nos ha costado más horas este año?',
        lineReliability: '¿Cuál es el MTBF de la línea de envasado?',
      },
      search: { subtitle: 'Activo' },
      notifications: {
        documentExpiring: 'Documentos de activos a punto de caducar',
        expiring: { title: 'Papeles a punto de caducar', body: '{count} · {titles}' },
        // El aviso más caro del ERP: cuesta dinero cada minuto que nadie lo lee.
        categories: { stopped: 'Una máquina se ha parado' },
        stopped: {
          title: 'Máquina parada',
          body: '{code} · {name}',
          bodyWithWho: '{code} · {name} — lo reporta {who}',
        },
      },

      actions: { new: 'Nuevo activo', edit: 'Editar', save: 'Guardar', cancelAction: 'Cancelar' },

      filters: {
        site: 'Centro', allSites: 'Todos',
        search: 'Buscar', searchPlaceholder: 'Código, nombre, número de serie…',
        level: 'Nivel', allLevels: 'Todos',
        type: 'Tipo', allTypes: 'Todos',
        criticality: 'Criticidad', allCriticalities: 'Todas',
      },

      levels: {
        building: 'Edificio', area: 'Área',
        line: 'Línea', machine: 'Máquina', component: 'Componente',
      },
      criticality: { 1: 'Crítico', 2: 'Alto', 3: 'Medio', 4: 'Bajo', 5: 'Muy bajo' },
      status: { active: 'En servicio', out_of_service: 'Parado', decommissioned: 'De baja' },

      tree: {
        root: 'Todo', down: 'parado',
        openBranch: 'Ver {count} dentro',
      },

      table: { asset: 'Activo', site: 'Centro', level: 'Nivel', criticality: 'Criticidad', status: 'Estado' },
      empty: {
        none: 'Aún no hay activos',
        noneHint: 'Empieza por los centros y ve colgando de ellos las naves, las líneas y las máquinas.',
        filtered: 'Nada con esos filtros',
        filteredHint: 'Prueba con menos filtros o busca por el código de la placa.',
      },
      pager: { previous: 'Anterior', next: 'Siguiente', of: 'Página {page} de {pages}' },

      tabs: {
        sheet: 'Ficha', meters: 'Contadores', documents: 'Documentos',
        history: 'Historial', reliability: 'Indicadores',
      },

      sheet: {
        loading: 'Cargando…',
        identity: 'Identificación',
        technical: 'Ficha técnica',
        parts: 'Despiece',
        part: 'Pieza', reference: 'Referencia', quantity: 'Cantidad',
        criticalPart: 'Crítica',
        label: 'Etiqueta',
        labelHint: 'Esto es lo que lee quien escanea el QR pegado en la máquina.',
      },

      form: {
        parts: 'Despiece',
        partsHint: 'Qué repuestos lleva. El almacén dice cuántos quedan; esto dice cuáles son',
        partPlaceholder: 'Busca en el almacén, o escríbelo',
        partReference: 'Referencia',
        partCritical: 'Crítico',
        addPart: 'Añadir repuesto',
        newTitle: 'Nuevo activo', editTitle: 'Editar activo', saved: 'Guardado',
        identity: 'Identificación', machine: 'La máquina', operation: 'Operación',
        technical: 'Ficha técnica', notes: 'Notas',
        code: 'Código', codeHint: 'El de la placa que ya está pegada en la máquina',
        name: 'Nombre',
        level: 'Nivel', levelHint: 'Es lo que permite comparar líneas entre sí',
        type: 'Tipo', noType: 'Sin tipo',
        parent: 'Cuelga de', noParent: 'Es raíz',
        site: 'Centro', siteHint: 'Se hereda de donde cuelgue', noSite: 'Sin centro',
        manufacturer: 'Fabricante', model: 'Modelo', serial: 'Número de serie',
        installedOn: 'Puesta en marcha',
        usefulLifeYears: 'Vida útil', years: 'años',
        responsible: 'Responsable', noResponsible: 'Sin responsable',
        criticality: 'Criticidad', criticalityHint: '1 para la que para la planta',
        dailyUseHours: 'Horas de uso al día',
        dailyUseHoursHint: 'De aquí salen todos los indicadores de disponibilidad',
        zeroHoursWarning: 'Con 0 horas al día no se puede calcular ninguna disponibilidad de esta máquina.',
        nfcCode: 'Código NFC o de barras', nfcHint: 'El que lleva la etiqueta física',
        yes: 'Sí', no: 'No',
      },

      meters: {
        none: 'Sin contadores',
        noneHint: 'Un contador es lo que permite mantener por uso: horas, kilómetros o ciclos.',
        log: 'Apuntar lectura', logTitle: 'Apuntar lectura', confirm: 'Apuntar',
        current: 'Ahora', lastRead: 'Última lectura', kind: 'Tipo',
        kinds: { cumulative: 'Acumulador', variable: 'Variable' },
        outOfRange: 'Fuera de rango',
        neverRead: 'Nunca leído',
        value: 'Lectura', readAt: 'Fecha', note: 'Nota',
        now: 'ahora marca {value}',
        willNotMove: 'Es menor que el contador: la lectura se guarda y el contador no se mueve.',
        wentBack: 'Lectura guardada. El contador no se ha movido porque era menor que el actual.',
        logged: 'Lectura guardada',
      },

      documents: {
        none: 'Sin documentos',
        noneHint: 'Manuales, esquemas, certificados y garantías de esta máquina.',
        document: 'Documento', kind: 'Tipo', validUntil: 'Vence',
        revision: 'rev. {revision}',
        kinds: {
          manual: 'Manual', scheme: 'Esquema', certificate: 'Certificado',
          warranty: 'Garantía', photo: 'Foto', other: 'Otro',
        },
      },

      history: {
        none: 'Sin historial',
        noneHint: 'Aquí se apunta todo lo que se le hace, venga de un plan, de una orden o de aquí mismo.',
        when: 'Cuándo', what: 'Qué', from: 'De dónde', hours: 'Horas',
        sources: { manual: 'A mano', work_order: 'Orden de trabajo', maintenance: 'Mantenimiento' },
      },

      reliability: {
        title: 'Indicadores', subtitle: 'Con la fórmula a la vista, para que se puedan discutir',
        availabilityByMaintenance: 'Disponibilidad por mantenimiento',
        availabilityByFailure: 'Disponibilidad por avería',
        mtbf: 'MTBF', mttr: 'MTTR',
        legend: 'HTP = horas del periodo · HPM = paro por mantenimiento · HPA = paro por avería · N = averías con paro',
        lastDays: 'Últimos {days} días',
        scope: 'Suma de {count} activos',
        byCause: 'Averías por causa', cause: 'Causa', howMany: 'Cuántas', withStop: 'Con paro',
        reasons: {
          NO_FAILURES: 'Sin averías en el periodo',
          NO_HOURS_IN_PERIOD: 'Sin horas en el periodo',
          DAILY_USE_HOURS_ZERO: 'Falta las horas de uso al día',
          UNKNOWN: 'No se puede calcular',
        },
      },

      stops: {
        report: 'Se ha averiado', resume: 'Ya funciona',
        reported: 'Avería apuntada', resumed: 'Máquina en servicio',
        downSince: 'Parada desde el {since}', downFor: 'lleva {time}',
      },

      failure: {
        classifyTitle: 'Clasificar la avería',
        classifyConfirm: 'Clasificarla',
        fromField: 'Del campo · {when}',
        noWords: 'Nadie escribió qué pasó',
        stopped: 'Paró',
        caught: 'Se cogió',
        stillDown: 'Sigue parada',
        title: 'Apuntar una avería', confirm: 'Apuntar', unknown: 'No lo sé',
        occurredAt: 'Cuándo pasó',
        didStop: '¿Se paró?', didStopHint: 'La máquina dejó de funcionar',
        type: 'Tipo de fallo', cause: 'Causa', detection: 'Cómo se detectó',
        severity: 'Gravedad', damage: 'Qué dañó',
        interruptionHours: 'Horas que paró a otros',
        interruptionHint: 'Lo que arrastró aguas abajo',
        solution: 'Qué se hizo', solutionHint: 'Se puede rellenar después',
      },

      plant: {
        toClassify: 'Averías sin clasificar',
        toClassifyHint: 'Llegaron de un parte con la hora y las palabras. Falta decir qué fueron',
        title: 'Estado de planta', subtitle: 'Qué está parado, qué falla más y qué papeles vencen',
        stoppedNow: 'Parado ahora mismo',
        nothingStopped: 'Nada parado', nothingStoppedHint: 'Todo lo que tenéis en el censo está en servicio.',
        asset: 'Activo', why: 'Por qué', since: 'Desde', forHowLong: 'Lleva',
        kinds: { breakdown: 'Avería', planned: 'Mantenimiento' },
        worst: 'Lo que más cuesta', worstHint: 'Por horas perdidas, no por número de averías',
        noFailures: 'Sin averías', noFailuresHint: 'No hay ninguna avería registrada en el periodo.',
        failures: 'Averías', hoursLost: 'Horas perdidas',
        expiring: 'Papeles que vencen', expiringHint: 'Certificados, garantías e inspecciones',
        noExpiring: 'Nada vence pronto', noExpiringHint: 'Ningún documento caduca dentro del aviso.',
      },
    },
    // ── La pantalla de Configuración ──────────────────────────────────
    // El backend declara cada ajuste con su etiqueta en inglés y manda
    // aquí la clave; sin estas líneas la pantalla se ve en inglés y no
    // falla nada. Los seis catálogos son las palabras del cliente.
    settings: {
      ns: {
        assets: {
          title: 'Activos',
          subtitle: 'Cómo llama esta empresa a lo que tiene y a lo que se le rompe',
          stopAlertRoles: {
            label: 'Quién se entera de que una máquina se ha parado',
            help: 'Solo las averías. Una parada planificada no es noticia.',
          },
          assetTypes: {
            label: 'Tipos de activo que tiene esta empresa',
            help: 'Son los que ofrece el selector de cada ficha.',
          },
          failureTypes: {
            label: 'Tipos de fallo',
            help: 'Los vuestros, no los nuestros: son el eje por el que se agrupa todo el análisis de averías.',
          },
          failureCauses: {
            label: 'Causas del fallo',
            help: 'Manténlas pocas: una lista de cuarenta es una lista que nadie lee hasta el final.',
          },
          detectionMethods: {
            label: 'Cómo se detectó el fallo',
            help: 'Es lo que dice si encontráis las cosas o esperáis a que paren la línea.',
          },
          failureSeverities: {
            label: 'Gravedad del fallo',
            help: 'La peor primero.',
          },
          damageTypes: {
            label: 'Qué dañó el fallo',
            help: 'Lo que costó el fallo además de la propia máquina.',
          },
          documentNoticeDays: {
            label: 'Días de antelación con los que un documento empieza a avisar de que vence',
            help: '0 apaga el aviso.',
          },
          criticalityAxes: {
            label: 'Puntuar la criticidad por cuatro ejes además de la global',
            help: 'Seguridad, producción, calidad y medio ambiente. Desactivado salvo que alguien los vaya a usar: una matriz que no rellena nadie es peor que un número que rellena todo el mundo.',
          },
        },
      },
    },
    audit: {
      actions: {
        asset: {
          created: 'Activo dado de alta',
          updated: 'Ficha del activo modificada',
          moved: 'Activo movido de sitio',
          status_changed: 'Estado del activo cambiado',
          document_added: 'Documento añadido al activo',
          meter_created: 'Contador creado',
          meter_updated: 'Contador modificado',
          reading_voided: 'Lectura anulada',
          stopped: 'Activo parado',
          resumed: 'Activo de nuevo en servicio',
          failure_recorded: 'Avería apuntada',
          failure_classified: 'Avería clasificada',
          stop_voided: 'Paro anulado',
          failure_voided: 'Avería anulada',
        },
      },
    },
  },

  ca: {
    // Como se llama este modulo en las pantallas de la plataforma
    // (el selector de la demo, los roles, la auditoria). Sin esto
    // salen listando el slug en ingles.
    modules: { assets: 'Actius' },
    assets: {
      title: 'Actius',
      // What the assistant's card says (backend/assistant.js, log_meter_reading).
      assistant: {
        reading: {
          action: 'Apuntar una lectura de comptador',
          summary: 'Lectura de {value} {unit} a {meter}',
          field: { meter: 'Comptador', value: 'Marca', current: 'Ara a', readAt: 'Llegida el', note: 'Nota' },
          where: 'Màquina',
          notes: { noMeter: 'No es troba aquest comptador; mira\'l a la fitxa de la màquina', below: 'Menor que el comptador: la lectura es desa i el comptador no es mou' },
          outcome: 'Lectura de {value} apuntada',
          outcomeKept: 'Lectura de {value} desada; el comptador no s\'ha mogut',
        },
      },
      dashboard: {
        stopped: 'Màquines parades',
        stoppedHelp: 'Quants equips estan parats en aquest moment, comptant avaries i parades programades.',
        expiring: 'Papers que caduquen',
        expiringHelp: 'Certificats, garanties i inspeccions que vencen dins del termini d\'avís configurat.',
        census: 'Equips al cens',
        censusHelp: 'Quants actius hi ha donats d\'alta, sense comptar els que estan de baixa.',
        stoppedNow: 'Parat ara mateix',
        stoppedNowHelp: 'Els equips parats en aquest moment, amb les hores que porten així. Estira la targeta i n\'hi caben més.',
        worst: 'Les que més costen',
        worstHelp: 'Els equips amb més hores de parada per avaria en l\'últim any, començant pel pitjor.',
      },
      subtitle: 'Quines màquines hi ha, on són i com es porten',
      nav: { census: 'Equips i instal·lacions' },
      suggestions: {
        whatIsDown: 'Què està aturat ara mateix?',
        worstMachine: 'Quina màquina ens ha costat més hores aquest any?',
        lineReliability: 'Quin és el MTBF de la línia d\'envasat?',
      },
      search: { subtitle: 'Actiu' },
      notifications: {
        documentExpiring: "Documents d'actius a punt de caducar",
        expiring: { title: 'Papers a punt de caducar', body: '{count} · {titles}' },
        categories: { stopped: "Una màquina s'ha aturat" },
        stopped: {
          title: 'Màquina aturada',
          body: '{code} · {name}',
          bodyWithWho: '{code} · {name} — ho reporta {who}',
        },
      },

      actions: { new: 'Nou actiu', edit: 'Editar', save: 'Desar', cancelAction: 'Cancel·lar' },

      filters: {
        site: 'Centre', allSites: 'Tots',
        search: 'Cercar', searchPlaceholder: 'Codi, nom, número de sèrie…',
        level: 'Nivell', allLevels: 'Tots',
        type: 'Tipus', allTypes: 'Tots',
        criticality: 'Criticitat', allCriticalities: 'Totes',
      },

      levels: {
        building: 'Edifici', area: 'Àrea',
        line: 'Línia', machine: 'Màquina', component: 'Component',
      },
      criticality: { 1: 'Crític', 2: 'Alt', 3: 'Mitjà', 4: 'Baix', 5: 'Molt baix' },
      status: { active: 'En servei', out_of_service: 'Aturat', decommissioned: 'De baixa' },

      tree: { root: 'Tot', down: 'aturat', openBranch: 'Veure\'n {count} a dins' },

      table: { asset: 'Actiu', site: 'Centre', level: 'Nivell', criticality: 'Criticitat', status: 'Estat' },
      empty: {
        none: 'Encara no hi ha actius',
        noneHint: 'Comença pels centres i penja-hi les naus, les línies i les màquines.',
        filtered: 'Res amb aquests filtres',
        filteredHint: 'Prova amb menys filtres o cerca pel codi de la placa.',
      },
      pager: { previous: 'Anterior', next: 'Següent', of: 'Pàgina {page} de {pages}' },

      tabs: {
        sheet: 'Fitxa', meters: 'Comptadors', documents: 'Documents',
        history: 'Historial', reliability: 'Indicadors',
      },

      sheet: {
        loading: 'Carregant…',
        identity: 'Identificació',
        technical: 'Fitxa tècnica',
        parts: 'Desglossament',
        part: 'Peça', reference: 'Referència', quantity: 'Quantitat',
        criticalPart: 'Crítica',
        label: 'Etiqueta',
        labelHint: 'Això és el que llegeix qui escaneja el QR enganxat a la màquina.',
      },

      form: {
        parts: 'Desglossament',
        partsHint: 'Quins recanvis porta. El magatzem diu quants en queden; això diu quins són',
        partPlaceholder: 'Cerca al magatzem, o escriu-ho',
        partReference: 'Referència',
        partCritical: 'Crític',
        addPart: 'Afegir recanvi',
        newTitle: 'Nou actiu', editTitle: 'Editar actiu', saved: 'Desat',
        identity: 'Identificació', machine: 'La màquina', operation: 'Operació',
        technical: 'Fitxa tècnica', notes: 'Notes',
        code: 'Codi', codeHint: 'El de la placa que ja hi ha enganxada a la màquina',
        name: 'Nom',
        level: 'Nivell', levelHint: 'És el que permet comparar línies entre elles',
        type: 'Tipus', noType: 'Sense tipus',
        parent: 'Penja de', noParent: 'És arrel',
        site: 'Centre', siteHint: 'S\'hereta d\'on pengi', noSite: 'Sense centre',
        manufacturer: 'Fabricant', model: 'Model', serial: 'Número de sèrie',
        installedOn: 'Posada en marxa',
        usefulLifeYears: 'Vida útil', years: 'anys',
        responsible: 'Responsable', noResponsible: 'Sense responsable',
        criticality: 'Criticitat', criticalityHint: '1 per a la que atura la planta',
        dailyUseHours: 'Hores d\'ús al dia',
        dailyUseHoursHint: 'D\'aquí surten tots els indicadors de disponibilitat',
        zeroHoursWarning: 'Amb 0 hores al dia no es pot calcular cap disponibilitat d\'aquesta màquina.',
        nfcCode: 'Codi NFC o de barres', nfcHint: 'El que porta l\'etiqueta física',
        yes: 'Sí', no: 'No',
      },

      meters: {
        none: 'Sense comptadors',
        noneHint: 'Un comptador és el que permet mantenir per ús: hores, quilòmetres o cicles.',
        log: 'Apuntar lectura', logTitle: 'Apuntar lectura', confirm: 'Apuntar',
        current: 'Ara', lastRead: 'Última lectura', kind: 'Tipus',
        kinds: { cumulative: 'Acumulador', variable: 'Variable' },
        outOfRange: 'Fora de rang',
        neverRead: 'Mai llegit',
        value: 'Lectura', readAt: 'Data', note: 'Nota',
        now: 'ara marca {value}',
        willNotMove: 'És menor que el comptador: la lectura es desa i el comptador no es mou.',
        wentBack: 'Lectura desada. El comptador no s\'ha mogut perquè era menor que l\'actual.',
        logged: 'Lectura desada',
      },

      documents: {
        none: 'Sense documents',
        noneHint: 'Manuals, esquemes, certificats i garanties d\'aquesta màquina.',
        document: 'Document', kind: 'Tipus', validUntil: 'Venç',
        revision: 'rev. {revision}',
        kinds: {
          manual: 'Manual', scheme: 'Esquema', certificate: 'Certificat',
          warranty: 'Garantia', photo: 'Foto', other: 'Altre',
        },
      },

      history: {
        none: 'Sense historial',
        noneHint: 'Aquí s\'apunta tot el que se li fa, vingui d\'un pla, d\'una ordre o d\'aquí mateix.',
        when: 'Quan', what: 'Què', from: 'D\'on', hours: 'Hores',
        sources: { manual: 'A mà', work_order: 'Ordre de treball', maintenance: 'Manteniment' },
      },

      reliability: {
        title: 'Indicadors', subtitle: 'Amb la fórmula a la vista, perquè es puguin discutir',
        availabilityByMaintenance: 'Disponibilitat per manteniment',
        availabilityByFailure: 'Disponibilitat per avaria',
        mtbf: 'MTBF', mttr: 'MTTR',
        legend: 'HTP = hores del període · HPM = aturada per manteniment · HPA = aturada per avaria · N = avaries amb aturada',
        lastDays: 'Últims {days} dies',
        scope: 'Suma de {count} actius',
        byCause: 'Avaries per causa', cause: 'Causa', howMany: 'Quantes', withStop: 'Amb aturada',
        reasons: {
          NO_FAILURES: 'Sense avaries en el període',
          NO_HOURS_IN_PERIOD: 'Sense hores en el període',
          DAILY_USE_HOURS_ZERO: 'Falten les hores d\'ús al dia',
          UNKNOWN: 'No es pot calcular',
        },
      },

      stops: {
        report: 'S\'ha avariat', resume: 'Ja funciona',
        reported: 'Avaria apuntada', resumed: 'Màquina en servei',
        downSince: 'Aturada des del {since}', downFor: 'fa {time}',
      },

      failure: {
        classifyTitle: 'Classificar l’avaria',
        classifyConfirm: 'Classificar-la',
        fromField: 'Del camp · {when}',
        noWords: 'Ningú no va escriure què va passar',
        stopped: 'Va parar',
        caught: 'Es va agafar',
        stillDown: 'Segueix parada',
        title: 'Apuntar una avaria', confirm: 'Apuntar', unknown: 'No ho sé',
        occurredAt: 'Quan va passar',
        didStop: 'S\'ha aturat?', didStopHint: 'La màquina va deixar de funcionar',
        type: 'Tipus de fallada', cause: 'Causa', detection: 'Com s\'ha detectat',
        severity: 'Gravetat', damage: 'Què va danyar',
        interruptionHours: 'Hores que va aturar altres',
        interruptionHint: 'El que va arrossegar aigües avall',
        solution: 'Què s\'ha fet', solutionHint: 'Es pot omplir després',
      },

      plant: {
        toClassify: 'Avaries sense classificar',
        toClassifyHint: 'Van arribar d’un comunicat amb l’hora i les paraules. Falta dir què van ser',
        title: 'Estat de planta', subtitle: 'Què està aturat, què falla més i quins papers vencen',
        stoppedNow: 'Aturat ara mateix',
        nothingStopped: 'Res aturat', nothingStoppedHint: 'Tot el que teniu al cens està en servei.',
        asset: 'Actiu', why: 'Per què', since: 'Des de', forHowLong: 'Fa',
        kinds: { breakdown: 'Avaria', planned: 'Manteniment' },
        worst: 'El que més costa', worstHint: 'Per hores perdudes, no per nombre d\'avaries',
        noFailures: 'Sense avaries', noFailuresHint: 'No hi ha cap avaria registrada en el període.',
        failures: 'Avaries', hoursLost: 'Hores perdudes',
        expiring: 'Papers que vencen', expiringHint: 'Certificats, garanties i inspeccions',
        noExpiring: 'Res venç aviat', noExpiringHint: 'Cap document caduca dins l\'avís.',
      },
    },
    settings: {
      ns: {
        assets: {
          title: 'Actius',
          subtitle: 'Com anomena aquesta empresa el que té i el que se li espatlla',
          stopAlertRoles: {
            label: 'Qui s’assabenta que una màquina s’ha aturat',
            help: 'Només les avaries. Una aturada planificada no és notícia.',
          },
          assetTypes: {
            label: 'Tipus d\'actiu que té aquesta empresa',
            help: 'Són els que ofereix el selector de cada fitxa.',
          },
          failureTypes: {
            label: 'Tipus de fallada',
            help: 'Els vostres, no els nostres: són l\'eix pel qual s\'agrupa tota l\'anàlisi d\'avaries.',
          },
          failureCauses: {
            label: 'Causes de la fallada',
            help: 'Manteniu-ne poques: una llista de quaranta és una llista que ningú no llegeix fins al final.',
          },
          detectionMethods: {
            label: 'Com es va detectar la fallada',
            help: 'És el que diu si trobeu les coses o espereu que aturin la línia.',
          },
          failureSeverities: {
            label: 'Gravetat de la fallada',
            help: 'La pitjor primer.',
          },
          damageTypes: {
            label: 'Què va danyar la fallada',
            help: 'El que va costar la fallada a més de la mateixa màquina.',
          },
          documentNoticeDays: {
            label: 'Dies d\'antelació amb què un document comença a avisar que venç',
            help: '0 apaga l\'avís.',
          },
          criticalityAxes: {
            label: 'Puntuar la criticitat per quatre eixos a més de la global',
            help: 'Seguretat, producció, qualitat i medi ambient. Desactivat tret que algú els hagi d\'usar: una matriu que no omple ningú és pitjor que un número que omple tothom.',
          },
        },
      },
    },
    audit: {
      actions: {
        asset: {
          created: 'Actiu donat d\'alta',
          updated: 'Fitxa de l\'actiu modificada',
          moved: 'Actiu mogut de lloc',
          status_changed: 'Estat de l\'actiu canviat',
          document_added: 'Document afegit a l\'actiu',
          meter_created: 'Comptador creat',
          meter_updated: 'Comptador modificat',
          reading_voided: 'Lectura anul·lada',
          stopped: 'Actiu aturat',
          resumed: 'Actiu de nou en servei',
          failure_recorded: 'Avaria apuntada',
          failure_classified: 'Avaria classificada',
          stop_voided: 'Aturada anul·lada',
          failure_voided: 'Avaria anul·lada',
        },
      },
    },
  },

  en: {
    // Como se llama este modulo en las pantallas de la plataforma
    // (el selector de la demo, los roles, la auditoria). Sin esto
    // salen listando el slug en ingles.
    modules: { assets: 'Assets' },
    assets: {
      title: 'Assets',
      // What the assistant's card says (backend/assistant.js, log_meter_reading).
      assistant: {
        reading: {
          action: 'File a meter reading',
          summary: 'Reading of {value} {unit} on {meter}',
          field: { meter: 'Meter', value: 'Reads', current: 'Now at', readAt: 'Read on', note: 'Note' },
          where: 'Machine',
          notes: { noMeter: 'That meter was not found; check it on the machine sheet', below: 'Lower than the counter: the reading is kept and the counter does not move' },
          outcome: 'Reading of {value} filed',
          outcomeKept: 'Reading of {value} kept; the counter did not move',
        },
      },
      dashboard: {
        stopped: 'Machines down',
        stoppedHelp: 'How many assets are stopped right now, counting breakdowns and planned stops alike.',
        expiring: 'Papers running out',
        expiringHelp: 'Certificates, warranties and inspections falling due within the configured notice period.',
        census: 'Assets on the census',
        censusHelp: 'How many assets are on file, retired ones excluded.',
        stoppedNow: 'Down right now',
        stoppedNowHelp: 'The assets stopped at this moment, with how long each has been that way. Stretch the card and more fit.',
        worst: 'The costliest ones',
        worstHelp: 'The assets with the most hours lost to breakdowns over the last year, worst first.',
      },
      subtitle: 'What machines there are, where they are and how they behave',
      nav: { census: 'Equipment & facilities' },
      suggestions: {
        whatIsDown: 'What is down right now?',
        worstMachine: 'Which machine has cost us the most hours this year?',
        lineReliability: 'What is the MTBF of the packing line?',
      },
      search: { subtitle: 'Asset' },
      notifications: {
        documentExpiring: 'Asset documents about to expire',
        expiring: { title: 'Documents about to expire', body: '{count} · {titles}' },
        categories: { stopped: 'A machine has stopped' },
        stopped: {
          title: 'Machine stopped',
          body: '{code} · {name}',
          bodyWithWho: '{code} · {name} — reported by {who}',
        },
      },

      actions: { new: 'New asset', edit: 'Edit', save: 'Save', cancelAction: 'Cancel' },

      filters: {
        site: 'Site', allSites: 'All',
        search: 'Search', searchPlaceholder: 'Code, name, serial number…',
        level: 'Level', allLevels: 'All',
        type: 'Kind', allTypes: 'All',
        criticality: 'Criticality', allCriticalities: 'All',
      },

      levels: {
        building: 'Building', area: 'Area',
        line: 'Line', machine: 'Machine', component: 'Component',
      },
      criticality: { 1: 'Critical', 2: 'High', 3: 'Medium', 4: 'Low', 5: 'Very low' },
      status: { active: 'In service', out_of_service: 'Down', decommissioned: 'Written off' },

      tree: { root: 'Everything', down: 'down', openBranch: 'Open {count} inside' },

      table: { asset: 'Asset', site: 'Site', level: 'Level', criticality: 'Criticality', status: 'State' },
      empty: {
        none: 'No assets yet',
        noneHint: 'Start with the sites and hang the buildings, lines and machines under them.',
        filtered: 'Nothing with those filters',
        filteredHint: 'Try fewer filters, or search by the code on the label.',
      },
      pager: { previous: 'Previous', next: 'Next', of: 'Page {page} of {pages}' },

      tabs: {
        sheet: 'Sheet', meters: 'Meters', documents: 'Documents',
        history: 'History', reliability: 'Indicators',
      },

      sheet: {
        loading: 'Loading…',
        identity: 'Identification',
        technical: 'Technical sheet',
        parts: 'Parts',
        part: 'Part', reference: 'Reference', quantity: 'Quantity',
        criticalPart: 'Critical',
        label: 'Label',
        labelHint: 'This is what whoever scans the QR stuck on the machine reads.',
      },

      form: {
        parts: 'Spare parts',
        partsHint: 'What it takes. The warehouse says how many are left; this says which ones they are',
        partPlaceholder: 'Search the warehouse, or type it',
        partReference: 'Reference',
        partCritical: 'Critical',
        addPart: 'Add a part',
        newTitle: 'New asset', editTitle: 'Edit asset', saved: 'Saved',
        identity: 'Identification', machine: 'The machine', operation: 'Operation',
        technical: 'Technical sheet', notes: 'Notes',
        code: 'Code', codeHint: 'The one on the label already stuck to the machine',
        name: 'Name',
        level: 'Level', levelHint: 'It is what lets lines be compared with each other',
        type: 'Kind', noType: 'No kind',
        parent: 'Hangs from', noParent: 'It is a root',
        site: 'Site', siteHint: 'Inherited from where it hangs', noSite: 'No site',
        manufacturer: 'Manufacturer', model: 'Model', serial: 'Serial number',
        installedOn: 'Commissioned on',
        usefulLifeYears: 'Useful life', years: 'years',
        responsible: 'Responsible', noResponsible: 'Nobody',
        criticality: 'Criticality', criticalityHint: '1 for the one that stops the plant',
        dailyUseHours: 'Hours of use per day',
        dailyUseHoursHint: 'Every availability figure is worked out from this',
        zeroHoursWarning: 'With 0 hours a day no availability can be worked out for this machine.',
        nfcCode: 'NFC or barcode', nfcHint: 'The one on the physical tag',
        yes: 'Yes', no: 'No',
      },

      meters: {
        none: 'No meters',
        noneHint: 'A meter is what makes maintenance by use possible: hours, kilometres or cycles.',
        log: 'File a reading', logTitle: 'File a reading', confirm: 'File',
        current: 'Now', lastRead: 'Last read', kind: 'Kind',
        kinds: { cumulative: 'Counter', variable: 'Variable' },
        outOfRange: 'Out of range',
        neverRead: 'Never read',
        value: 'Reading', readAt: 'Date', note: 'Note',
        now: 'now reads {value}',
        willNotMove: 'It is lower than the counter: the reading is filed and the counter does not move.',
        wentBack: 'Reading filed. The counter did not move because it was lower than the current one.',
        logged: 'Reading filed',
      },

      documents: {
        none: 'No documents',
        noneHint: 'Manuals, schemes, certificates and warranties for this machine.',
        document: 'Document', kind: 'Kind', validUntil: 'Expires',
        revision: 'rev. {revision}',
        kinds: {
          manual: 'Manual', scheme: 'Scheme', certificate: 'Certificate',
          warranty: 'Warranty', photo: 'Photo', other: 'Other',
        },
      },

      history: {
        none: 'No history',
        noneHint: 'Everything done to it is filed here, whether it came from a plan, an order or this screen.',
        when: 'When', what: 'What', from: 'From', hours: 'Hours',
        sources: { manual: 'By hand', work_order: 'Work order', maintenance: 'Maintenance' },
      },

      reliability: {
        title: 'Indicators', subtitle: 'With the formula in plain sight, so they can be argued with',
        availabilityByMaintenance: 'Availability by maintenance',
        availabilityByFailure: 'Availability by failure',
        mtbf: 'MTBF', mttr: 'MTTR',
        legend: 'HTP = hours in the period · HPM = stopped for maintenance · HPA = stopped by failure · N = failures with a stop',
        lastDays: 'Last {days} days',
        scope: 'Sum of {count} assets',
        byCause: 'Failures by cause', cause: 'Cause', howMany: 'How many', withStop: 'With a stop',
        reasons: {
          NO_FAILURES: 'No failures in the period',
          NO_HOURS_IN_PERIOD: 'No hours in the period',
          DAILY_USE_HOURS_ZERO: 'Hours of use per day is missing',
          UNKNOWN: 'Cannot be worked out',
        },
      },

      stops: {
        report: 'It has broken down', resume: 'It works again',
        reported: 'Breakdown filed', resumed: 'Machine back in service',
        downSince: 'Down since {since}', downFor: 'for {time}',
      },

      failure: {
        classifyTitle: 'Code the failure',
        classifyConfirm: 'Code it',
        fromField: 'From the field · {when}',
        noWords: 'Nobody wrote down what happened',
        stopped: 'Stopped',
        caught: 'Caught',
        stillDown: 'Still down',
        title: 'File a breakdown', confirm: 'File', unknown: 'I do not know',
        occurredAt: 'When it happened',
        didStop: 'Did it stop?', didStopHint: 'The machine stopped working',
        type: 'Kind of failure', cause: 'Cause', detection: 'How it was noticed',
        severity: 'Severity', damage: 'What it damaged',
        interruptionHours: 'Hours it stopped others',
        interruptionHint: 'What it dragged down downstream',
        solution: 'What was done', solutionHint: 'Can be filled in later',
      },

      plant: {
        toClassify: 'Failures to code',
        toClassifyHint: 'They came in from a parte with the hour and the words. Somebody has to say what they were',
        title: 'Plant status', subtitle: 'What is down, what fails most and what papers expire',
        stoppedNow: 'Down right now',
        nothingStopped: 'Nothing down', nothingStoppedHint: 'Everything in the census is in service.',
        asset: 'Asset', why: 'Why', since: 'Since', forHowLong: 'For',
        kinds: { breakdown: 'Breakdown', planned: 'Maintenance' },
        worst: 'What costs most', worstHint: 'By hours lost, not by number of failures',
        noFailures: 'No failures', noFailuresHint: 'No failure recorded in the period.',
        failures: 'Failures', hoursLost: 'Hours lost',
        expiring: 'Papers expiring', expiringHint: 'Certificates, warranties and inspections',
        noExpiring: 'Nothing expiring', noExpiringHint: 'No document expires inside the notice window.',
      },
    },
    settings: {
      ns: {
        assets: {
          title: 'Assets',
          subtitle: 'What this company calls what it owns, and what goes wrong with it',
          stopAlertRoles: {
            label: 'Who hears that a machine has stopped',
            help: 'Only breakdowns. A planned window is not news.',
          },
          assetTypes: {
            label: 'Kinds of asset this company has',
            help: 'They are what the selector offers on each asset sheet.',
          },
          failureTypes: {
            label: 'Kinds of failure',
            help: 'Yours, not ours — they are the axis every failure analysis groups by.',
          },
          failureCauses: {
            label: 'Causes of failure',
            help: 'Keep the list short: a list of forty is a list nobody reads to the end.',
          },
          detectionMethods: {
            label: 'How a failure was noticed',
            help: 'It is what tells you whether you find things or wait for them to stop the line.',
          },
          failureSeverities: {
            label: 'How serious a failure was',
            help: 'Worst first.',
          },
          damageTypes: {
            label: 'What a failure damaged',
            help: 'What the failure cost beyond the machine itself.',
          },
          documentNoticeDays: {
            label: 'Days ahead a document starts warning that it runs out',
            help: '0 turns the warning off.',
          },
          criticalityAxes: {
            label: 'Score criticality on four axes as well as overall',
            help: 'Safety, production, quality and environment. Off unless somebody is going to use them: a matrix nobody fills in is worse than one number everybody does.',
          },
        },
      },
    },
    audit: {
      actions: {
        asset: {
          created: 'Asset registered',
          updated: 'Asset sheet changed',
          moved: 'Asset moved in the tree',
          status_changed: 'Asset state changed',
          document_added: 'Document added to the asset',
          meter_created: 'Meter created',
          meter_updated: 'Meter changed',
          reading_voided: 'Reading voided',
          stopped: 'Asset stopped',
          resumed: 'Asset back in service',
          failure_recorded: 'Breakdown filed',
          failure_classified: 'Failure classified',
          stop_voided: 'Stop voided',
          failure_voided: 'Breakdown voided',
        },
      },
    },
  },

  fr: {
    // Como se llama este modulo en las pantallas de la plataforma
    // (el selector de la demo, los roles, la auditoria). Sin esto
    // salen listando el slug en ingles.
    modules: { assets: 'Actifs' },
    assets: {
      title: 'Actifs',
      // What the assistant's card says (backend/assistant.js, log_meter_reading).
      assistant: {
        reading: {
          action: 'Saisir un relevé de compteur',
          summary: 'Relevé de {value} {unit} sur {meter}',
          field: { meter: 'Compteur', value: 'Indique', current: 'Actuellement à', readAt: 'Relevé le', note: 'Note' },
          where: 'Machine',
          notes: { noMeter: 'Ce compteur est introuvable ; vérifiez-le sur la fiche de la machine', below: 'Inférieur au compteur : le relevé est gardé et le compteur ne bouge pas' },
          outcome: 'Relevé de {value} saisi',
          outcomeKept: 'Relevé de {value} gardé ; le compteur n\'a pas bougé',
        },
      },
      dashboard: {
        stopped: 'Machines à l\'arrêt',
        stoppedHelp: 'Combien d\'équipements sont à l\'arrêt en ce moment, pannes et arrêts programmés compris.',
        expiring: 'Papiers qui expirent',
        expiringHelp: 'Certificats, garanties et inspections arrivant à échéance dans le délai d\'alerte configuré.',
        census: 'Équipements au recensement',
        censusHelp: 'Combien d\'actifs sont enregistrés, hors ceux qui sont sortis du parc.',
        stoppedNow: 'À l\'arrêt en ce moment',
        stoppedNowHelp: 'Les équipements arrêtés en ce moment, avec le temps écoulé depuis. Étirez la carte et il en tient plus.',
        worst: 'Ceux qui coûtent le plus',
        worstHelp: 'Les équipements avec le plus d\'heures d\'arrêt pour panne sur la dernière année, le pire en tête.',
      },
      subtitle: 'Quelles machines il y a, où elles sont et comment elles se comportent',
      nav: { census: 'Équipements et installations' },
      suggestions: {
        whatIsDown: 'Qu\'est-ce qui est à l\'arrêt en ce moment ?',
        worstMachine: 'Quelle machine nous a coûté le plus d\'heures cette année ?',
        lineReliability: 'Quel est le MTBF de la ligne de conditionnement ?',
      },
      search: { subtitle: 'Actif' },
      notifications: {
        documentExpiring: "Documents d'actifs sur le point d'expirer",
        expiring: { title: "Documents sur le point d'expirer", body: '{count} · {titles}' },
        categories: { stopped: "Une machine s'est arrêtée" },
        stopped: {
          title: 'Machine arrêtée',
          body: '{code} · {name}',
          bodyWithWho: '{code} · {name} — signalé par {who}',
        },
      },

      actions: { new: 'Nouvel actif', edit: 'Modifier', save: 'Enregistrer', cancelAction: 'Annuler' },

      filters: {
        site: 'Site', allSites: 'Tous',
        search: 'Rechercher', searchPlaceholder: 'Code, nom, numéro de série…',
        level: 'Niveau', allLevels: 'Tous',
        type: 'Type', allTypes: 'Tous',
        criticality: 'Criticité', allCriticalities: 'Toutes',
      },

      levels: {
        building: 'Bâtiment', area: 'Zone',
        line: 'Ligne', machine: 'Machine', component: 'Composant',
      },
      criticality: { 1: 'Critique', 2: 'Élevée', 3: 'Moyenne', 4: 'Faible', 5: 'Très faible' },
      status: { active: 'En service', out_of_service: 'À l\'arrêt', decommissioned: 'Retiré' },

      tree: { root: 'Tout', down: 'à l\'arrêt', openBranch: 'Voir les {count} à l\'intérieur' },

      table: { asset: 'Actif', site: 'Site', level: 'Niveau', criticality: 'Criticité', status: 'État' },
      empty: {
        none: 'Pas encore d\'actifs',
        noneHint: 'Commencez par les sites et accrochez-y les bâtiments, les lignes et les machines.',
        filtered: 'Rien avec ces filtres',
        filteredHint: 'Essayez avec moins de filtres, ou cherchez par le code de la plaque.',
      },
      pager: { previous: 'Précédent', next: 'Suivant', of: 'Page {page} sur {pages}' },

      tabs: {
        sheet: 'Fiche', meters: 'Compteurs', documents: 'Documents',
        history: 'Historique', reliability: 'Indicateurs',
      },

      sheet: {
        loading: 'Chargement…',
        identity: 'Identification',
        technical: 'Fiche technique',
        parts: 'Nomenclature',
        part: 'Pièce', reference: 'Référence', quantity: 'Quantité',
        criticalPart: 'Critique',
        label: 'Étiquette',
        labelHint: 'C\'est ce que lit celui qui scanne le QR collé sur la machine.',
      },

      form: {
        parts: 'Pièces détachées',
        partsHint: 'Ce qu’elle prend. Le magasin dit combien il en reste ; ceci dit lesquelles',
        partPlaceholder: 'Cherchez au magasin, ou tapez-le',
        partReference: 'Référence',
        partCritical: 'Critique',
        addPart: 'Ajouter une pièce',
        newTitle: 'Nouvel actif', editTitle: 'Modifier l\'actif', saved: 'Enregistré',
        identity: 'Identification', machine: 'La machine', operation: 'Exploitation',
        technical: 'Fiche technique', notes: 'Notes',
        code: 'Code', codeHint: 'Celui de la plaque déjà collée sur la machine',
        name: 'Nom',
        level: 'Niveau', levelHint: 'C\'est ce qui permet de comparer les lignes entre elles',
        type: 'Type', noType: 'Sans type',
        parent: 'Rattaché à', noParent: 'C\'est une racine',
        site: 'Site', siteHint: 'Hérité de son rattachement', noSite: 'Sans site',
        manufacturer: 'Fabricant', model: 'Modèle', serial: 'Numéro de série',
        installedOn: 'Mise en service',
        usefulLifeYears: 'Durée de vie', years: 'ans',
        responsible: 'Responsable', noResponsible: 'Personne',
        criticality: 'Criticité', criticalityHint: '1 pour celle qui arrête l\'usine',
        dailyUseHours: 'Heures d\'utilisation par jour',
        dailyUseHoursHint: 'Tous les indicateurs de disponibilité en découlent',
        zeroHoursWarning: 'Avec 0 heure par jour, aucune disponibilité ne peut être calculée pour cette machine.',
        nfcCode: 'Code NFC ou code-barres', nfcHint: 'Celui que porte l\'étiquette physique',
        yes: 'Oui', no: 'Non',
      },

      meters: {
        none: 'Aucun compteur',
        noneHint: 'Un compteur est ce qui permet la maintenance à l\'usage : heures, kilomètres ou cycles.',
        log: 'Saisir un relevé', logTitle: 'Saisir un relevé', confirm: 'Saisir',
        current: 'Maintenant', lastRead: 'Dernier relevé', kind: 'Type',
        kinds: { cumulative: 'Compteur', variable: 'Variable' },
        outOfRange: 'Hors plage',
        neverRead: 'Jamais relevé',
        value: 'Relevé', readAt: 'Date', note: 'Note',
        now: 'affiche {value}',
        willNotMove: 'C\'est inférieur au compteur : le relevé est conservé et le compteur ne bouge pas.',
        wentBack: 'Relevé conservé. Le compteur n\'a pas bougé car la valeur était inférieure à l\'actuelle.',
        logged: 'Relevé enregistré',
      },

      documents: {
        none: 'Aucun document',
        noneHint: 'Manuels, schémas, certificats et garanties de cette machine.',
        document: 'Document', kind: 'Type', validUntil: 'Expire',
        revision: 'rév. {revision}',
        kinds: {
          manual: 'Manuel', scheme: 'Schéma', certificate: 'Certificat',
          warranty: 'Garantie', photo: 'Photo', other: 'Autre',
        },
      },

      history: {
        none: 'Aucun historique',
        noneHint: 'Tout ce qui lui est fait est consigné ici, que cela vienne d\'un plan, d\'un ordre ou de cet écran.',
        when: 'Quand', what: 'Quoi', from: 'D\'où', hours: 'Heures',
        sources: { manual: 'À la main', work_order: 'Ordre de travail', maintenance: 'Maintenance' },
      },

      reliability: {
        title: 'Indicateurs', subtitle: 'Avec la formule en vue, pour qu\'on puisse en discuter',
        availabilityByMaintenance: 'Disponibilité par maintenance',
        availabilityByFailure: 'Disponibilité par panne',
        mtbf: 'MTBF', mttr: 'MTTR',
        legend: 'HTP = heures de la période · HPM = arrêt pour maintenance · HPA = arrêt par panne · N = pannes avec arrêt',
        lastDays: '{days} derniers jours',
        scope: 'Somme de {count} actifs',
        byCause: 'Pannes par cause', cause: 'Cause', howMany: 'Combien', withStop: 'Avec arrêt',
        reasons: {
          NO_FAILURES: 'Aucune panne sur la période',
          NO_HOURS_IN_PERIOD: 'Aucune heure sur la période',
          DAILY_USE_HOURS_ZERO: 'Les heures d\'utilisation par jour manquent',
          UNKNOWN: 'Impossible à calculer',
        },
      },

      stops: {
        report: 'Elle est tombée en panne', resume: 'Elle refonctionne',
        reported: 'Panne enregistrée', resumed: 'Machine remise en service',
        downSince: 'À l\'arrêt depuis le {since}', downFor: 'depuis {time}',
      },

      failure: {
        classifyTitle: 'Coder la panne',
        classifyConfirm: 'La coder',
        fromField: 'Du terrain · {when}',
        noWords: 'Personne n’a écrit ce qui s’est passé',
        stopped: 'Arrêt',
        caught: 'Détectée',
        stillDown: 'Toujours à l’arrêt',
        title: 'Enregistrer une panne', confirm: 'Enregistrer', unknown: 'Je ne sais pas',
        occurredAt: 'Quand est-ce arrivé',
        didStop: 'S\'est-elle arrêtée ?', didStopHint: 'La machine a cessé de fonctionner',
        type: 'Type de panne', cause: 'Cause', detection: 'Comment détectée',
        severity: 'Gravité', damage: 'Ce qu\'elle a endommagé',
        interruptionHours: 'Heures d\'arrêt causées à d\'autres',
        interruptionHint: 'Ce qu\'elle a entraîné en aval',
        solution: 'Ce qui a été fait', solutionHint: 'Peut être rempli plus tard',
      },

      plant: {
        toClassify: 'Pannes à coder',
        toClassifyHint: 'Arrivées d’un rapport avec l’heure et les mots. Il reste à dire ce que c’était',
        title: 'État de l\'usine', subtitle: 'Ce qui est à l\'arrêt, ce qui tombe le plus en panne et les papiers qui expirent',
        stoppedNow: 'À l\'arrêt en ce moment',
        nothingStopped: 'Rien à l\'arrêt', nothingStoppedHint: 'Tout ce qui est au recensement est en service.',
        asset: 'Actif', why: 'Pourquoi', since: 'Depuis', forHowLong: 'Depuis',
        kinds: { breakdown: 'Panne', planned: 'Maintenance' },
        worst: 'Ce qui coûte le plus', worstHint: 'En heures perdues, pas en nombre de pannes',
        noFailures: 'Aucune panne', noFailuresHint: 'Aucune panne enregistrée sur la période.',
        failures: 'Pannes', hoursLost: 'Heures perdues',
        expiring: 'Papiers qui expirent', expiringHint: 'Certificats, garanties et inspections',
        noExpiring: 'Rien n\'expire bientôt', noExpiringHint: 'Aucun document n\'expire dans la fenêtre d\'alerte.',
      },
    },
    settings: {
      ns: {
        assets: {
          title: 'Actifs',
          subtitle: 'Comment cette entreprise nomme ce qu\'elle possède et ce qui tombe en panne',
          stopAlertRoles: {
            label: 'Qui apprend qu’une machine s’est arretee',
            help: 'Uniquement les pannes. Un arret planifie n’est pas une nouvelle.',
          },
          assetTypes: {
            label: 'Types d\'actif que possède cette entreprise',
            help: 'Ce sont ceux que propose le sélecteur de chaque fiche.',
          },
          failureTypes: {
            label: 'Types de panne',
            help: 'Les vôtres, pas les nôtres : c\'est l\'axe sur lequel se regroupe toute l\'analyse des pannes.',
          },
          failureCauses: {
            label: 'Causes de la panne',
            help: 'Gardez la liste courte : une liste de quarante est une liste que personne ne lit jusqu\'au bout.',
          },
          detectionMethods: {
            label: 'Comment la panne a été détectée',
            help: 'C\'est ce qui dit si vous trouvez les choses ou si vous attendez qu\'elles arrêtent la ligne.',
          },
          failureSeverities: {
            label: 'Gravité de la panne',
            help: 'La pire en premier.',
          },
          damageTypes: {
            label: 'Ce que la panne a endommagé',
            help: 'Ce que la panne a coûté au-delà de la machine elle-même.',
          },
          documentNoticeDays: {
            label: 'Jours d\'avance à partir desquels un document signale qu\'il expire',
            help: '0 désactive l\'alerte.',
          },
          criticalityAxes: {
            label: 'Noter la criticité sur quatre axes en plus de la note globale',
            help: 'Sécurité, production, qualité et environnement. Désactivé sauf si quelqu\'un va s\'en servir : une matrice que personne ne remplit vaut moins qu\'un chiffre que tout le monde remplit.',
          },
        },
      },
    },
    audit: {
      actions: {
        asset: {
          created: 'Actif enregistré',
          updated: 'Fiche de l\'actif modifiée',
          moved: 'Actif déplacé dans l\'arbre',
          status_changed: 'État de l\'actif changé',
          document_added: 'Document ajouté à l\'actif',
          meter_created: 'Compteur créé',
          meter_updated: 'Compteur modifié',
          reading_voided: 'Relevé annulé',
          stopped: 'Actif à l\'arrêt',
          resumed: 'Actif remis en service',
          failure_recorded: 'Panne enregistrée',
          failure_classified: 'Panne classée',
          stop_voided: 'Arrêt annulé',
          failure_voided: 'Panne annulée',
        },
      },
    },
  },
}

// El vocabulario de analítica del módulo, aparte porque es un contrato con el
// catálogo de `backend/analytics.js` y no copia de una pantalla. Ver ese fichero.
import { withAnalytics } from './i18n/analytics.js'

// What the assistant panel says while this module's tools run: another
// contract with the backend (backend/assistant.js), in its own file.
import { withToolLabels } from './i18n/toolLabels.js'
// The reasons and field codes its errors carry (frontend/i18n/errorReasons.js).
import { withErrorReasons } from './i18n/errorReasons.js'

export const messages = withErrorReasons(withToolLabels(withAnalytics(baseMessages, 'assets')))
